import axios from 'axios';
import api from '@/api';
import mainBus from '@/micro/eventBus';
import { getLogo } from '@/utils/intConfiguration';
import { rmToken } from '@/utils/authStorage';

const baseURL = '/boss';
const CUSTOMER_SAAS_ACTIVE_RULE = '/customerSaas/';
const CUSTOMER_SAAS_LOGOUT_REDIRECT_EVENT = 'customer-saas-get-logout-redirect';
const CUSTOMER_SAAS_LOGOUT_REDIRECT_TIMEOUT = 3000;
const CUSTOMER_SAAS_APP_ID = '10016';
const LOGOUT_REDIRECT_CONFIG_API = '/client-bff/web/open/api/tenant/config/custom/query';
const LOGOUT_REDIRECT_PARENT_CODE = 'GLOBAL_CONFIG';
const LOGOUT_REDIRECT_CODE = 'LOGOUT_REDIRECT_URL';
const LOGOUT_REDIRECT_CACHE_KEY = 'customer-saas-logout-redirect-url';

const getLogoutBaseURL = () => {
  if (process.env.NODE_ENV !== 'development') {
    return baseURL;
  }
  const curEnv = localStorage.getItem('curEnv') || '';
  return `${baseURL}${curEnv ? `/${curEnv}` : ''}`;
};

const getLogoutHeaders = () => ({
  'Content-Type': 'application/json; charset=UTF-8',
  'X-Auth-AppId': localStorage.getItem('appId') || getLogo().appId,
  'X-Auth-Token': localStorage.getItem('token') || '',
});

const postWithoutAuthRedirect = async (url, data) => {
  const res = await axios({
    method: 'post',
    url,
    data,
    baseURL: getLogoutBaseURL(),
    timeout: 20000,
    headers: getLogoutHeaders(),
  });
  const body = res?.data;
  if (body?.errCode && body.errCode !== '0') {
    throw body;
  }
  return body?.data ?? body?.record ?? body;
};

const isCustomerSaasRoute = (path = window.location.pathname) => (
  String(path || '').startsWith(CUSTOMER_SAAS_ACTIVE_RULE)
);

const isCustomerSaasLogoutContext = (routePath) => (
  isCustomerSaasRoute(routePath)
  || isCustomerSaasRoute(window.location.pathname)
  || String(localStorage.getItem('appId') || '') === CUSTOMER_SAAS_APP_ID
);

const getCurrentOriginForLogoutRedirect = () => {
  try {
    if (typeof window === 'undefined') return '';
    return String((window.location && window.location.origin) || '').trim();
  } catch (e) {
    return '';
  }
};

const getLogoutRedirectCacheKey = () => {
  const origin = getCurrentOriginForLogoutRedirect();
  return origin ? `${origin}:${LOGOUT_REDIRECT_CACHE_KEY}` : LOGOUT_REDIRECT_CACHE_KEY;
};

const getCachedLogoutRedirectUrl = () => {
  try {
    const cacheKey = getLogoutRedirectCacheKey();
    const sessionRedirectUrl = String(sessionStorage.getItem(cacheKey) || '').trim();
    if (sessionRedirectUrl) return sessionRedirectUrl;

    const localRedirectUrl = String(localStorage.getItem(cacheKey) || '').trim();
    if (localRedirectUrl) {
      sessionStorage.setItem(cacheKey, localRedirectUrl);
    }
    return localRedirectUrl;
  } catch (e) {
    return '';
  }
};

const removeLogoutRedirectCache = (storage, key) => {
  try {
    if (storage) storage.removeItem(key);
  } catch (e) {
    console.warn('[logout redirect][main] cache cleanup failed', e);
  }
};

const setCachedLogoutRedirectUrl = (url) => {
  const redirectUrl = String(url || '').trim();
  const cacheKey = getLogoutRedirectCacheKey();
  try {
    if (!redirectUrl) {
      removeLogoutRedirectCache(sessionStorage, cacheKey);
      removeLogoutRedirectCache(localStorage, cacheKey);
      removeLogoutRedirectCache(sessionStorage, LOGOUT_REDIRECT_CACHE_KEY);
      removeLogoutRedirectCache(localStorage, LOGOUT_REDIRECT_CACHE_KEY);
      return;
    }

    sessionStorage.setItem(cacheKey, redirectUrl);
    localStorage.setItem(cacheKey, redirectUrl);
    removeLogoutRedirectCache(sessionStorage, LOGOUT_REDIRECT_CACHE_KEY);
    removeLogoutRedirectCache(localStorage, LOGOUT_REDIRECT_CACHE_KEY);
  } catch (e) {
    console.warn('[logout redirect][main] cache write failed', e);
  }
};

const parseLogoutRedirectUrl = (res) => {
  const list = Array.isArray(res) ? res : (res?.data || res?.record || []);
  const items = Array.isArray(list)
    ? list.flatMap((item) => {
      if (Array.isArray(item?.children) && item.children.length) return item.children;
      return item ? [item] : [];
    })
    : [];
  const target = items.find((item) => String(item?.code || '').trim() === LOGOUT_REDIRECT_CODE);
  return String(target?.value || '').trim();
};

const fetchCustomerSaasLogoutRedirectUrl = async () => {
  const params = {
    parentCodes: [LOGOUT_REDIRECT_PARENT_CODE],
  };
  let tenantId = '';
  try {
    const info = JSON.parse(localStorage.getItem('user_info') || '{}');
    tenantId = String(info?.tenantId || info?.customerId || localStorage.getItem('tenantId') || '').trim();
  } catch (e) {
    tenantId = String(localStorage.getItem('tenantId') || '').trim();
  }
  if (tenantId) {
    params.tenantId = tenantId;
  }
  console.log('[logout redirect][main] fallback request config', params);
  const res = await postWithoutAuthRedirect(LOGOUT_REDIRECT_CONFIG_API, params).catch((e) => {
    console.error('[logout redirect][main] fallback request failed', e);
    return [];
  });
  console.log('[logout redirect][main] fallback config response', res);
  const redirectUrl = parseLogoutRedirectUrl(res);
  setCachedLogoutRedirectUrl(redirectUrl);
  console.log('[logout redirect][main] fallback parsed redirectUrl', redirectUrl);
  return redirectUrl;
};

const getCustomerSaasLogoutRedirectUrl = (routePath) => {
  const currentPath = window.location.pathname;
  const matched = isCustomerSaasLogoutContext(routePath);
  const listenerCount = mainBus?.events?.[CUSTOMER_SAAS_LOGOUT_REDIRECT_EVENT]?.length || 0;
  console.log('[logout redirect][main] check route', {
    routePath,
    currentPath,
    matched,
    appId: localStorage.getItem('appId'),
    listenerCount,
  });

  if (!matched) {
    return Promise.resolve('');
  }

  const cachedRedirectUrl = getCachedLogoutRedirectUrl();
  if (cachedRedirectUrl) {
    console.log('[logout redirect][main] cached redirectUrl', cachedRedirectUrl);
    return Promise.resolve(cachedRedirectUrl);
  }

  if (!listenerCount) {
    return fetchCustomerSaasLogoutRedirectUrl();
  }

  return new Promise((resolve) => {
    let settled = false;
    let timer = null;
    const done = (url = '') => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      console.log('[logout redirect][main] callback url', String(url || '').trim());
      resolve(String(url || '').trim());
    };
    timer = setTimeout(() => {
      console.warn('[logout redirect][main] wait child config timeout');
      done('');
    }, CUSTOMER_SAAS_LOGOUT_REDIRECT_TIMEOUT);

    console.log('[logout redirect][main] emit event', CUSTOMER_SAAS_LOGOUT_REDIRECT_EVENT);
    mainBus.$emit(CUSTOMER_SAAS_LOGOUT_REDIRECT_EVENT, {
      callback: done,
    });
  });
};

const callLoginOut = async () => {
  if (!api.loginOut) return;
  await postWithoutAuthRedirect(api.loginOut, {}).catch(() => {});
};

export const logoutWithRedirect = async ({
  router,
  routePath,
  reloadOnFallback = false,
  callLoginOut: shouldCallLoginOut = true,
  fallbackToLogin = true,
} = {}) => {
  localStorage.removeItem('loginSource');
  console.log('[logout redirect][main] start logout', {
    routePath,
    pathname: window.location.pathname,
    reloadOnFallback,
    shouldCallLoginOut,
  });
  const redirectUrl = await getCustomerSaasLogoutRedirectUrl(routePath);
  console.log('[logout redirect][main] resolved redirectUrl', redirectUrl);

  if (!redirectUrl && router) {
    await router.push('/login');
  }

  if (shouldCallLoginOut) {
    await callLoginOut();
  }
  rmToken();

  if (redirectUrl) {
    window.location.href = redirectUrl;
    return { redirected: true, redirectUrl };
  }

  if (!router && fallbackToLogin) {
    window.location.href = '/login';
  }

  if (reloadOnFallback) {
    window.location.reload();
  }

  return { redirected: false, redirectUrl: '' };
};

export {
  CUSTOMER_SAAS_LOGOUT_REDIRECT_EVENT,
  isCustomerSaasRoute,
};
